import React, { useState } from "react";
import {
  ChatWrapper,
  MessageBubble,
  ChatInputWrapper,
  Input,
  MessageContainer,
  Avatar,
  MessageContent,
  MessageActions,
} from "./chatInterface.styled";
import SendIcon from "@mui/icons-material/Send";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import PersonIcon from "@mui/icons-material/Person";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import { fetchBaseUrl } from "core/utils/common";
import CustomMarkdown from "core/components/global/markdown/markdown";

interface Message {
  content: string;
  isUser: boolean;
  timestamp: Date;
}

interface ChatInterfaceProps {
  userPrompt: string;
  setUserPrompt: (value: string) => void;
  conversationId: string;
  theme: any;
}

const baseURL = fetchBaseUrl({port: 8000});

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  userPrompt,
  setUserPrompt,
  conversationId,
  theme,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // ...existing code...

 const handleSend = async () => {
  if (!userPrompt.trim()) return;

  // Add user message
  const newMessage: Message = {
    content: userPrompt,
    isUser: true,
    timestamp: new Date(),
  };

  setMessages((prev) => [...prev, newMessage]);
  setIsLoading(true);

  try {
    const response = await fetch(`${baseURL}/socai/api/serve?stream=true`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-API-Key": "dev-key",
      },
      body: JSON.stringify({
        user_input: userPrompt,
        conversationId: conversationId
      }),
    });

    if (!response.body) {
      throw new Error("No response body");
    }

    // Add an empty assistant message for streaming
    setMessages((prev) => [
      ...prev,
      {
        content: "",
        isUser: false,
        timestamp: new Date(),
      },
    ]);

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let done = false;

    while (!done) {
      const { value, done: doneReading } = await reader.read();
      done = doneReading;
      if (value) {
        const chunk = decoder.decode(value, { stream: true });
        setMessages((prev) => {
          // Update the last assistant message with new chunk
          const updated = [...prev];
          const lastIdx = updated.length - 1;
          if (lastIdx >= 0 && !updated[lastIdx].isUser) {
            updated[lastIdx] = {
              ...updated[lastIdx],
              content: updated[lastIdx].content + chunk,
            };
          }
          return updated;
        });
      }
    }
  } catch (error) {
    console.error("Failed to get response:", error);
    setMessages((prev) => [
      ...prev,
      {
        content:
          "Sorry, I encountered an error while processing your request.",
        isUser: false,
        timestamp: new Date(),
      },
    ]);
  } finally {
    setIsLoading(false);
    setUserPrompt("");
  }
};

  // ...existing code...

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <ChatWrapper>
      {messages.map((message, index) => (
        <div className="message" key={index}>
          <MessageContainer isUser={message.isUser}>
            <Avatar isUser={message.isUser}>
              {message.isUser ? (
                <PersonIcon />
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 49 49"
                  width={80}
                  height={80}
                  fill="#ffffff"
                >
                  <path d="M 33 28 L 31 30 L 30 30 L 30 31 L 31 31 L 32 32 L 32 33 L 33 34 L 33 33 L 35 31 L 36 31 L 36 30 L 35 30 Z" />
                  <path d="M 21 14 L 21 15 L 20 16 L 20 18 L 18 20 L 17 20 L 16 21 L 15 21 L 14 22 L 13 22 L 14 23 L 15 23 L 16 24 L 17 24 L 20 27 L 20 28 L 21 29 L 21 30 L 22 31 L 22 30 L 23 29 L 23 28 L 24 27 L 24 26 L 26 24 L 27 24 L 28 23 L 29 23 L 30 22 L 29 21 L 28 21 L 27 20 L 26 20 L 23 17 L 23 15 L 22 14 Z" />
                </svg>
              )}
            </Avatar>
            <MessageContent isUser={message.isUser}>
              <MessageBubble isUser={message.isUser}>
                <CustomMarkdown content={message.content} showEditButton={false} />
              </MessageBubble>
              {!message.isUser && (
                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <MessageActions>
                    <IconButton size="small">
                      <ThumbUpIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small">
                      <ThumbDownIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small">
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small">
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </MessageActions>
                </div>
              )}
            </MessageContent>
          </MessageContainer>
        </div>
      ))}

      {isLoading && (
        <MessageContainer isUser={false}>
          <Avatar isUser={false}>
            <SmartToyIcon />
          </Avatar>
          <MessageContent>
            <MessageBubble isUser={false}>
              <div className="typing-indicator">Generating response...</div>
            </MessageBubble>
          </MessageContent>
        </MessageContainer>
      )}

      <ChatInputWrapper>
        <Input
          value={userPrompt}
          onChange={(e) => setUserPrompt(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Chat with Soc ai..."
        />
        <IconButton
          onClick={handleSend}
          disabled={!userPrompt.trim() || isLoading}
          style={{
            color: theme.colors.textColor,
            opacity: !userPrompt.trim() || isLoading ? 0.5 : 1,
          }}
        >
          <SendIcon />
        </IconButton>
      </ChatInputWrapper>
    </ChatWrapper>
  );
};

export default ChatInterface;
